import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "fileInput",
    "selected",
    "fileArea",
    "progressTrack",
    "filePreview",
    "uploadingTitle",
    "uploadingFileName",
    "uploadingState",
    "defaultIcon",
    "defaultTitle",
    "defaultHint",
    "columnsValue",
    "rowsValue",
    "ignoredValue",
    "ignoredInfo",
    "spreadsheetPreview",
    "genericPreview",
    "fileType",
    "fileSize",
    "fileSelector",
  ]

  static values = { types: Array, size: Number, maxRows: { type: Number, default: 100000 } }

  // This is to capture drag events anywhere on the page
  connect() {
    document.body.addEventListener("dragover", this.handleDragOver.bind(this))
    document.body.addEventListener("dragleave", this.handleDragLeave.bind(this))
    document.body.addEventListener("drop", this.handleDrop.bind(this))

    // Dispatch event on initial load to disable button
    this.dispatchFileEvent(false)
  }

  disconnect() {
    document.body.removeEventListener("dragover", this.handleDragOver.bind(this))
    document.body.removeEventListener("dragleave", this.handleDragLeave.bind(this))
    document.body.removeEventListener("drop", this.handleDrop.bind(this))
  }

  // Dispatch custom event for file selection status
  dispatchFileEvent(isFileSelected) {
    const event = new CustomEvent("file-upload:status-changed", {
      bubbles: true,
      detail: {
        isFileSelected: isFileSelected,
      },
    })
    this.element.dispatchEvent(event)
  }

  handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()

    // Check if files are being dragged
    if (e.dataTransfer.types.includes("Files")) {
      this.fileAreaTarget.classList.add("is-dragover")
    }
  }

  handleDragLeave(e) {
    e.preventDefault()
    e.stopPropagation()

    // Remove dragover state when leaving the area
    this.fileAreaTarget.classList.remove("is-dragover")
  }

  handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    this.fileAreaTarget.classList.remove("is-dragover")
    this.dropFile(e)
  }

  dropFile(e) {
    this.fileInputTarget.files = e.dataTransfer.files

    // We manually trigger the "change" event as else it's not triggered
    // by the browser and we don't enter the "selectFile" function.
    this.fileInputTarget.dispatchEvent(new Event("change"))
  }

  openFileSelection() {
    this.fileInputTarget.click()
  }

  selectFile(e) {
    const acceptedExtensions = this.typesValue
    const maxSize = this.sizeValue
    const file = e.target.files[0]

    if (!file) return

    const nameSplitted = file.name.split(".")
    const extension = nameSplitted[nameSplitted.length - 1].toLowerCase()

    if (acceptedExtensions.indexOf(extension) === -1) {
      let message
      if (acceptedExtensions.length === 1) {
        message = i18n.t("js.back.file_upload_enhanced.invalid_extension_single", { extension: acceptedExtensions[0] })
      } else {
        message = i18n.t("js.back.file_upload_enhanced.invalid_extension_multiple", {
          extensions: acceptedExtensions.join(", "),
        })
      }

      this.fileInputTarget.value = ""
      this.displayFlash("danger", message)
      this.dispatchFileEvent(false)
    } else if (file.size > maxSize) {
      const message = i18n.t("js.back.file_upload_enhanced.file_too_large", { size: (maxSize / 1000000).toFixed(0) })

      this.fileInputTarget.value = ""
      this.displayFlash("danger", message)
      this.dispatchFileEvent(false)
    } else {
      // Show uploading state
      this.showUploadingState(file)

      // Begin file parsing
      this.parseFile(file)
    }
  }

  displayFlash(type, message) {
    App.displayFlash(type, message)
  }

  showUploadingState(file) {
    this.fileAreaTarget.classList.add("is-uploading")

    if (this.hasFileSelectorTarget) {
      this.fileSelectorTarget.classList.add("d-none")
    }

    if (this.hasDefaultIconTarget) {
      this.defaultIconTarget.classList.add("d-none")
    }

    if (this.hasDefaultTitleTarget) {
      this.defaultTitleTarget.classList.add("d-none")
    }

    if (this.hasDefaultHintTarget) {
      this.defaultHintTarget.classList.add("d-none")
    }

    if (this.hasUploadingStateTarget) {
      this.uploadingStateTarget.classList.remove("d-none")
    }

    if (this.hasUploadingFileNameTarget) {
      this.uploadingFileNameTarget.textContent = file.name
    }

    if (this.hasProgressTrackTarget) {
      this.progressTrackTarget.style.width = "0%"
      this.progressTrackTarget.setAttribute("aria-valuenow", "0")
    }
  }

  parseFile(file) {
    const reader = new FileReader()

    reader.onprogress = (event) => {
      if (event.lengthComputable) {
        // Calculate and update progress percentage (first 50% is reading file)
        const progressPercentage = Math.round(((event.loaded / event.total) * 100) / 2)
        this.updateProgress(progressPercentage)
      }
    }

    reader.onload = (event) => {
      const content = event.target.result
      this.processFileContent(content, file)
    }

    reader.onerror = () => {
      this.fileInputTarget.value = ""
      this.displayFlash("danger", i18n.t("js.back.file_upload_enhanced.read_error"))
      this.resetUploadState()
      this.dispatchFileEvent(false)
    }

    // Start reading the file
    reader.readAsText(file)
  }

  processFileContent(content, file) {
    // Start at 50% progress since file reading is complete
    let progress = 50
    this.updateProgress(progress)

    try {
      // Split content into lines
      const lines = content.split(/\r\n|\n|\r/)

      // Filter out empty lines
      const nonEmptyLines = lines.filter((line) => line.trim().length > 0)

      if (nonEmptyLines.length === 0) {
        throw new Error(i18n.t("js.back.file_upload_enhanced.empty_file"))
      }

      // Detect the delimiter (comma or semicolon)
      const firstLine = nonEmptyLines[0]
      const delimiter = firstLine.includes(";") ? ";" : ","

      // Simple column count from the first line
      const columnCount = this.parseCSVLine(firstLine, delimiter).length

      // Data rows count (excluding the header)
      const dataRowCount = nonEmptyLines.length - 1

      // Rows are only counted as ignored if total exceeds maxRows value
      const maxRows = this.maxRowsValue
      const ignoredRows = dataRowCount > maxRows ? dataRowCount - maxRows : 0

      // Gradually increase progress to 100%
      const progressInterval = setInterval(() => {
        progress += 5
        if (progress >= 100) {
          clearInterval(progressInterval)

          // Show file preview with the file data
          this.showFilePreviewWithData(file, {
            columns: columnCount,
            rows: dataRowCount,
            ignored: ignoredRows,
          })
        }
        this.updateProgress(progress)
      }, 50)
    } catch (error) {
      this.fileInputTarget.value = ""
      this.displayFlash("danger", i18n.t("js.back.file_upload_enhanced.processing_error", { error: error.message }))
      this.resetUploadState()
      this.dispatchFileEvent(false)
    }
  }

  parseCSVLine(line, delimiter) {
    const result = []
    let current = ""
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]

      if (char === '"') {
        inQuotes = !inQuotes
      } else if (char === delimiter && !inQuotes) {
        result.push(current)
        current = ""
      } else {
        current += char
      }
    }

    // Add the last field
    result.push(current)

    return result
  }

  updateProgress(percentage) {
    if (this.hasProgressTrackTarget) {
      this.progressTrackTarget.style.width = `${percentage}%`
      this.progressTrackTarget.setAttribute("aria-valuenow", percentage.toString())
    }
  }

  showFilePreviewWithData(file, data) {
    // Start animation sequence
    this.animateUploadComplete(file, data)
  }

  animateUploadComplete(file, data) {
    // Remove the small class to trigger animation
    this.uploadingStateTarget.classList.remove("file-preview--small")

    // After animation completes, swap components
    setTimeout(() => {
      // Hide file area with uploading state
      this.fileAreaTarget.classList.remove("is-uploading")
      this.fileSelectorTarget.classList.add("d-none")

      // Hide uploading state
      if (this.hasUploadingStateTarget) {
        this.uploadingStateTarget.classList.add("d-none")
      }

      // Show file preview
      if (this.hasFilePreviewTarget) {
        // Update file name and details
        this.selectedTarget.textContent = file.name

        // Format the file size in KB or MB
        const fileSize =
          file.size < 1000000 ? `${(file.size / 1024).toFixed(1)} kB` : `${(file.size / 1048576).toFixed(1)} MB`

        // Update file meta information
        const fileMetaElement = this.selectedTarget.nextElementSibling
        if (fileMetaElement) {
          fileMetaElement.textContent = i18n.t("js.back.file_upload_enhanced.file_meta", {
            ext: file.name.split(".").pop().toUpperCase(),
            size: fileSize,
          })
        }

        const isSpreadsheet = file.type.includes("csv") || file.type.includes("txt")

        if (isSpreadsheet) {
          this.spreadsheetPreviewTarget.classList.remove("d-none")
          this.genericPreviewTarget.classList.add("d-none")

          // Update count values with actual data
          if (this.hasColumnsValueTarget) {
            this.columnsValueTarget.textContent = data.columns
          }

          if (this.hasRowsValueTarget) {
            this.rowsValueTarget.textContent = this.formatNumber(data.rows)
          }

          // Only show ignored rows if there are any
          if (this.hasIgnoredValueTarget) {
            this.ignoredValueTarget.textContent = this.formatNumber(data.ignored)

            // Hide or show the ignored info based on whether there are ignored rows
            if (this.hasIgnoredInfoTarget) {
              if (data.ignored > 0) {
                this.ignoredInfoTarget.classList.remove("d-none")
              } else {
                this.ignoredInfoTarget.classList.add("d-none")
              }
            }
          }
        } else {
          this.fileTypeTarget.textContent = file.type
          this.fileSizeTarget.textContent = fileSize

          this.genericPreviewTarget.classList.remove("d-none")
          this.spreadsheetPreviewTarget.classList.add("d-none")
        }

        // Show the file preview section
        this.filePreviewTarget.classList.remove("d-none")

        // Dispatch event that file is valid and processed
        this.dispatchFileEvent(true)
      }
    }, 200) // Wait for CSS transition to complete
  }

  formatNumber(number) {
    // Format number with thousands separators
    return new Intl.NumberFormat().format(number)
  }

  resetUploadState() {
    // Show default elements
    if (this.hasDefaultIconTarget) {
      this.defaultIconTarget.classList.remove("d-none")
    }

    if (this.hasDefaultTitleTarget) {
      this.defaultTitleTarget.classList.remove("d-none")
    }

    if (this.hasDefaultHintTarget) {
      this.defaultHintTarget.classList.remove("d-none")
    }

    // Hide uploading state and reset to small size
    if (this.hasUploadingStateTarget) {
      this.uploadingStateTarget.classList.add("d-none")
      this.uploadingStateTarget.classList.add("file-preview--small")
    }
  }

  closeSelected(e) {
    // Prevent normal button action if this is triggered by a button
    if (e) e.preventDefault()

    // Reset file input
    this.fileInputTarget.value = ""

    // Hide file preview
    if (this.hasFilePreviewTarget) {
      this.filePreviewTarget.classList.add("d-none")
    }

    // Reset file area
    this.fileSelectorTarget.classList.remove("d-none")
    this.fileAreaTarget.classList.remove("is-uploading")

    // Show default elements
    if (this.hasDefaultIconTarget) {
      this.defaultIconTarget.classList.remove("d-none")
    }

    if (this.hasDefaultTitleTarget) {
      this.defaultTitleTarget.classList.remove("d-none")
    }

    if (this.hasDefaultHintTarget) {
      this.defaultHintTarget.classList.remove("d-none")
    }

    // Hide uploading state and reset to small size
    if (this.hasUploadingStateTarget) {
      this.uploadingStateTarget.classList.add("d-none")
      this.uploadingStateTarget.classList.add("file-preview--small")
    }

    // Dispatch event that file is removed
    this.dispatchFileEvent(false)
  }
}
